import { useState, useEffect, useCallback, useRef } from "react"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Badge } from "../../components/ui/badge"
import { Card } from "../../components/ui/card"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog"
import {
  Search,
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  GraduationCap,
  Eye,
  Loader2,
  Users2,
  ChevronLeft,
  ChevronRight,
  X,
  UserCircle,
  Calendar,
  Hash,
  Award,
  Globe,
  FileText,
  Building2,
  Clock,
} from "lucide-react"
import { getPublicJobApplicationsByJobId } from "../../services/candidateapi"
import { toast } from "react-hot-toast"
import { useRbac } from "../../context"
import type { Application } from "../../components/types/application.types"
import type { DashboardRestoreState } from "../../utils/dashboardRestoreState"

interface PaginationState {
  page: number
  limit: number
  totalPages: number
  totalResults: number
}

export default function JobCandidates() {
  const navigate = useNavigate()
  const { id: jobId } = useParams<{ id: string }>()
  const location = useLocation()
  const { user } = useRbac()
  const jobTitle = location.state?.jobTitle || "Job"
  const dashboardRestore = location.state?.dashboardRestore as DashboardRestoreState | undefined

  const [applications, setApplications] = useState<Application[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalResults: 0,
  })
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const fetchIdRef = useRef(0)

  const fetchCandidates = useCallback(async () => {
    if (!jobId) return

    const fetchId = ++fetchIdRef.current
    setIsLoading(true)
    setError(null)

    try {
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        _t: Date.now(),
      }

      const response = await getPublicJobApplicationsByJobId(jobId, params)

      if (fetchId !== fetchIdRef.current) return

      const apps = (response.data.applications as unknown as Application[]) ?? []
      setApplications(apps)
      setPagination((prev) => ({
        ...prev,
        totalPages: response.totalPages,
        totalResults: response.totalResults,
      }))
    } catch (err: any) {
      if (fetchId !== fetchIdRef.current) return
      console.error("Failed to fetch candidates:", err)
      setError(err.response?.data?.message || "Failed to load candidates")
      toast.error("Failed to load candidates")
    } finally {
      if (fetchId === fetchIdRef.current) {
        setIsLoading(false)
      }
    }
  }, [jobId, pagination.page, pagination.limit, searchTerm, user])

  useEffect(() => {
    fetchCandidates()
  }, [fetchCandidates])

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }))
    }
  }

  const getStatusBadgeVariant = (status: string) => {
    if (status === "Joined") return "default"
    if (status === "Selected" || status === "Offered") return "default"
    if (status === "Rejected" || status === "Rejected by Company") return "destructive"
    return "secondary"
  }

  const getStatusBadgeColor = (status: string) => {
    if (status === "Joined") return "bg-green-500 hover:bg-green-600"
    if (status === "Selected" || status === "Offered") return "bg-blue-500 hover:bg-blue-600"
    return ""
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() =>
              navigate("/dashboard", {
                state: dashboardRestore ? { dashboardRestore } : {},
              })
            }
            className="mb-5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors duration-200"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="bg-gradient-to-r from-white to-gray-50 rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-[#EC6E51]/10 flex items-center justify-center">
                    <Users2 className="h-5 w-5 text-[#EC6E51]" />
                  </div>
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                      Candidates for {jobTitle}
                    </h1>
                    <p className="text-sm text-gray-600 mt-1">
                      View and manage all candidates who have applied for this position
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-white rounded-lg px-5 py-3 shadow-sm border border-gray-200">
                <div className="w-10 h-10 rounded-lg bg-[#EC6E51]/10 flex items-center justify-center">
                  <Users2 className="h-5 w-5 text-[#EC6E51]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 leading-none">{pagination.totalResults}</p>
                  <p className="text-xs text-gray-500 font-medium mt-1">Total Candidates</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              type="text"
              placeholder="Search by name, email, or phone number..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setPagination((prev) => ({ ...prev, page: 1 }))
              }}
              className="pl-11 pr-4 py-2.5 h-11 border-gray-300 focus:border-[#EC6E51] focus:ring-[#EC6E51]/20 rounded-lg transition-all duration-200"
            />
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="relative">
              <Loader2 className="h-10 w-10 animate-spin text-[#EC6E51]" />
              <div className="absolute inset-0 rounded-full border-2 border-[#EC6E51]/20"></div>
            </div>
            <span className="mt-4 text-gray-600 font-medium">Loading candidates...</span>
            <p className="mt-1 text-sm text-gray-500">Please wait while we fetch the data</p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Candidates</h3>
            <p className="text-red-700 mb-6 max-w-md mx-auto">{error}</p>
            <Button 
              onClick={fetchCandidates} 
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-100 px-6 py-2.5 rounded-lg font-medium transition-all duration-200"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && applications.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Users2 className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No candidates found</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              {searchTerm
                ? "No candidates match your search criteria. Try adjusting your search terms."
                : "No candidates have applied for this job yet."}
            </p>
          </div>
        )}

        {/* Candidates List */}
        {!isLoading && !error && applications.length > 0 && (
          <>
            <div className="space-y-3 mb-6">
              {applications.map((application) => (
                <Card 
                  key={application.applicationId} 
                  className="group border border-gray-200 hover:border-[#EC6E51]/30 hover:shadow-lg transition-all duration-300 overflow-hidden bg-white"
                >
                  <div className="p-6">
                    <div className="flex items-start gap-5">
                      {/* Avatar Section */}
                      <div className="flex-shrink-0">
                        <div className="relative">
                          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#EC6E51] to-[#EC6E51]/80 flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-[#EC6E51]/20 group-hover:shadow-xl group-hover:shadow-[#EC6E51]/30 transition-shadow duration-300">
                            {application.candidate?.name?.charAt(0)?.toUpperCase() || "N"}
                          </div>
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full border-2 border-white flex items-center justify-center">
                            <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                          </div>
                        </div>
                      </div>

                      {/* Main Content */}
                      <div className="flex-1 min-w-0">
                        {/* Header Row */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <h3 className="text-lg font-semibold text-gray-900 leading-tight">
                                {application.candidate?.name || "Unknown Candidate"}
                              </h3>
                              <Badge
                                variant={getStatusBadgeVariant(application.currentStatus?.status || "Applied")}
                                className={`${getStatusBadgeColor(application.currentStatus?.status || "Applied")} text-xs font-medium px-2.5 py-0.5 rounded-md shadow-sm`}
                              >
                                {application.currentStatus?.status || "Applied"}
                              </Badge>
                            </div>
                            {application.appliedOn && (
                              <p className="text-xs text-gray-500 font-medium">
                                Applied on {new Date(application.appliedOn).toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric"
                                })}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Contact Information Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 mb-4">
                          {application.candidate?.email && (
                            <div className="flex items-center gap-2.5 text-gray-700 group/item">
                              <div className="w-8 h-8 rounded-lg bg-gray-100 group-hover/item:bg-[#EC6E51]/10 flex items-center justify-center transition-colors duration-200">
                                <Mail className="h-4 w-4 text-gray-500 group-hover/item:text-[#EC6E51] transition-colors duration-200" />
                              </div>
                              <span className="text-sm font-medium truncate">{application.candidate.email}</span>
                            </div>
                          )}
                          {application.candidate?.phone && (
                            <div className="flex items-center gap-2.5 text-gray-700 group/item">
                              <div className="w-8 h-8 rounded-lg bg-gray-100 group-hover/item:bg-[#EC6E51]/10 flex items-center justify-center transition-colors duration-200">
                                <Phone className="h-4 w-4 text-gray-500 group-hover/item:text-[#EC6E51] transition-colors duration-200" />
                              </div>
                              <span className="text-sm font-medium">{application.candidate.phone}</span>
                            </div>
                          )}
                          {application.candidate?.currentLocationDetails?.city && (
                            <div className="flex items-center gap-2.5 text-gray-700 group/item">
                              <div className="w-8 h-8 rounded-lg bg-gray-100 group-hover/item:bg-[#EC6E51]/10 flex items-center justify-center transition-colors duration-200">
                                <MapPin className="h-4 w-4 text-gray-500 group-hover/item:text-[#EC6E51] transition-colors duration-200" />
                              </div>
                              <span className="text-sm font-medium">{application.candidate.currentLocationDetails.city}</span>
                            </div>
                          )}
                          {application.candidate?.totalExperienceYears !== undefined && (
                            <div className="flex items-center gap-2.5 text-gray-700 group/item">
                              <div className="w-8 h-8 rounded-lg bg-gray-100 group-hover/item:bg-[#EC6E51]/10 flex items-center justify-center transition-colors duration-200">
                                <Briefcase className="h-4 w-4 text-gray-500 group-hover/item:text-[#EC6E51] transition-colors duration-200" />
                              </div>
                              <span className="text-sm font-medium">
                                {application.candidate.totalExperienceYears} {application.candidate.totalExperienceYears === 1 ? 'year' : 'years'} experience
                              </span>
                            </div>
                          )}
                          {application.candidate?.highestQualification && (
                            <div className="flex items-center gap-2.5 text-gray-700 group/item">
                              <div className="w-8 h-8 rounded-lg bg-gray-100 group-hover/item:bg-[#EC6E51]/10 flex items-center justify-center transition-colors duration-200">
                                <GraduationCap className="h-4 w-4 text-gray-500 group-hover/item:text-[#EC6E51] transition-colors duration-200" />
                              </div>
                              <span className="text-sm font-medium">{application.candidate.highestQualification}</span>
                            </div>
                          )}
                        </div>

                        {/* Skills Section */}
                        {application.candidate?.skills && application.candidate.skills.length > 0 && (
                          <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                            {application.candidate.skills.slice(0, 6).map((skill, idx) => (
                              <Badge 
                                key={idx} 
                                variant="outline" 
                                className="text-xs font-medium px-2.5 py-1 bg-gray-50 text-gray-700 border-gray-200 hover:bg-[#EC6E51]/5 hover:border-[#EC6E51]/30 hover:text-[#EC6E51] transition-all duration-200"
                              >
                                {skill}
                              </Badge>
                            ))}
                            {application.candidate.skills.length > 6 && (
                              <Badge 
                                variant="outline" 
                                className="text-xs font-medium px-2.5 py-1 bg-gray-50 text-gray-600 border-gray-200"
                              >
                                +{application.candidate.skills.length - 6} more
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Action Button */}
                      <div className="flex-shrink-0 ml-4">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => {
                            setSelectedApplication(application)
                            setIsDetailsModalOpen(true)
                          }}
                          className="bg-[#EC6E51] hover:bg-[#EC6E51]/90 text-white font-medium px-5 py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-600 font-medium">
                    Showing <span className="text-gray-900 font-semibold">{(pagination.page - 1) * pagination.limit + 1}</span> to{" "}
                    <span className="text-gray-900 font-semibold">{Math.min(pagination.page * pagination.limit, pagination.totalResults)}</span> of{" "}
                    <span className="text-gray-900 font-semibold">{pagination.totalResults}</span> candidates
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1 || isLoading}
                      className="border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-lg transition-all duration-200"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        let pageNum: number
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1
                        } else if (pagination.page >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i
                        } else {
                          pageNum = pagination.page - 2 + i
                        }
                        return (
                          <Button
                            key={pageNum}
                            variant={pagination.page === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            disabled={isLoading}
                            className={
                              pagination.page === pageNum
                                ? "bg-[#EC6E51] hover:bg-[#EC6E51]/90 text-white border-[#EC6E51] shadow-sm px-4 py-2 rounded-lg transition-all duration-200"
                                : "border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-lg transition-all duration-200"
                            }
                          >
                            {pageNum}
                          </Button>
                        )
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages || isLoading}
                      className="border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-lg transition-all duration-200"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Candidate Details Modal */}
        <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
            {selectedApplication && (
              <div className="bg-white rounded-2xl">
                {/* Header */}
                <div className="bg-gradient-to-r from-[#EC6E51] to-[#EC6E51]/90 p-6 text-white">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white font-bold text-2xl border-2 border-white/30">
                      {selectedApplication.candidate?.name?.charAt(0)?.toUpperCase() || "N"}
                    </div>
                    <div className="flex-1">
                      <DialogTitle className="text-2xl font-bold text-white mb-2">
                        {selectedApplication.candidate?.name || "Unknown Candidate"}
                      </DialogTitle>
                      <DialogDescription className="text-white/90 text-sm">
                        Complete candidate profile and application details
                      </DialogDescription>
                    </div>
                  </div>
                  
                  {/* Status Badge */}
                  <div className="flex items-center gap-3">
                    <Badge
                      variant={getStatusBadgeVariant(selectedApplication.currentStatus?.status || "Applied")}
                      className={`${getStatusBadgeColor(selectedApplication.currentStatus?.status || "Applied")} text-sm font-medium px-3 py-1 bg-white/20 backdrop-blur-sm border-white/30`}
                    >
                      {selectedApplication.currentStatus?.status || "Applied"}
                    </Badge>
                    {selectedApplication.appliedOn && (
                      <span className="text-white/80 text-sm">
                        Applied on {new Date(selectedApplication.appliedOn).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric"
                        })}
                      </span>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                  {/* Personal Information */}
                  <div className="bg-gray-50 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <UserCircle className="h-5 w-5 text-[#EC6E51]" />
                      <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedApplication.candidate?.email && (
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                            <Mail className="h-5 w-5 text-[#EC6E51]" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium mb-1">Email</p>
                            <p className="text-sm font-medium text-gray-900">{selectedApplication.candidate.email}</p>
                          </div>
                        </div>
                      )}
                      {selectedApplication.candidate?.phone && (
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                            <Phone className="h-5 w-5 text-[#EC6E51]" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium mb-1">Phone</p>
                            <p className="text-sm font-medium text-gray-900">{selectedApplication.candidate.phone}</p>
                          </div>
                        </div>
                      )}
                      {selectedApplication.candidate?.fatherName && (
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                            <User className="h-5 w-5 text-[#EC6E51]" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium mb-1">Father's Name</p>
                            <p className="text-sm font-medium text-gray-900">{selectedApplication.candidate.fatherName}</p>
                          </div>
                        </div>
                      )}
                      {selectedApplication.candidate?.dateOfBirth && (
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                            <Calendar className="h-5 w-5 text-[#EC6E51]" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium mb-1">Date of Birth</p>
                            <p className="text-sm font-medium text-gray-900">
                              {new Date(selectedApplication.candidate.dateOfBirth).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "long",
                                year: "numeric"
                              })}
                            </p>
                          </div>
                        </div>
                      )}
                      {selectedApplication.candidate?.gender && (
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                            <UserCircle className="h-5 w-5 text-[#EC6E51]" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium mb-1">Gender</p>
                            <p className="text-sm font-medium text-gray-900">{selectedApplication.candidate.gender}</p>
                          </div>
                        </div>
                      )}
                      {selectedApplication.candidate?.aadharNumber && (
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                            <Hash className="h-5 w-5 text-[#EC6E51]" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium mb-1">Aadhar Number</p>
                            <p className="text-sm font-medium text-gray-900">{selectedApplication.candidate.aadharNumber}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Location Information */}
                  {selectedApplication.candidate?.currentLocationDetails && (
                    <div className="bg-gray-50 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <MapPin className="h-5 w-5 text-[#EC6E51]" />
                        <h3 className="text-lg font-semibold text-gray-900">Location</h3>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                          <MapPin className="h-5 w-5 text-[#EC6E51]" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 font-medium mb-1">Current Location</p>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedApplication.candidate.currentLocationDetails.fullAddress || selectedApplication.candidate.currentLocationDetails.city || "Not specified"}
                          </p>
                          {selectedApplication.candidate.currentLocationDetails.city && (
                            <p className="text-xs text-gray-500 mt-1">City: {selectedApplication.candidate.currentLocationDetails.city}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Education & Experience */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedApplication.candidate?.highestQualification && (
                      <div className="bg-gray-50 rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <GraduationCap className="h-5 w-5 text-[#EC6E51]" />
                          <h3 className="text-lg font-semibold text-gray-900">Education</h3>
                        </div>
                        <p className="text-sm font-medium text-gray-900">{selectedApplication.candidate.highestQualification}</p>
                      </div>
                    )}
                    {(selectedApplication.candidate?.totalExperienceYears !== undefined || selectedApplication.candidate?.totalExperienceMonths !== undefined) && (
                      <div className="bg-gray-50 rounded-xl p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <Briefcase className="h-5 w-5 text-[#EC6E51]" />
                          <h3 className="text-lg font-semibold text-gray-900">Experience</h3>
                        </div>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedApplication.candidate.totalExperienceYears || 0} {selectedApplication.candidate.totalExperienceYears === 1 ? 'year' : 'years'}
                          {selectedApplication.candidate.totalExperienceMonths ? ` ${selectedApplication.candidate.totalExperienceMonths} ${selectedApplication.candidate.totalExperienceMonths === 1 ? 'month' : 'months'}` : ''}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Skills */}
                  {selectedApplication.candidate?.skills && selectedApplication.candidate.skills.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <Award className="h-5 w-5 text-[#EC6E51]" />
                        <h3 className="text-lg font-semibold text-gray-900">Skills</h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selectedApplication.candidate.skills.map((skill, idx) => (
                          <Badge 
                            key={idx} 
                            variant="outline" 
                            className="text-sm font-medium px-3 py-1.5 bg-white text-gray-700 border-gray-300 hover:bg-[#EC6E51]/5 hover:border-[#EC6E51]/30 hover:text-[#EC6E51] transition-all duration-200"
                          >
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Languages */}
                  {selectedApplication.candidate?.spokenLanguages && selectedApplication.candidate.spokenLanguages.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <Globe className="h-5 w-5 text-[#EC6E51]" />
                        <h3 className="text-lg font-semibold text-gray-900">Languages</h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selectedApplication.candidate.spokenLanguages.map((language, idx) => (
                          <Badge 
                            key={idx} 
                            variant="outline" 
                            className="text-sm font-medium px-3 py-1.5 bg-white text-gray-700 border-gray-300"
                          >
                            {language}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Application Details */}
                  <div className="bg-gray-50 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <FileText className="h-5 w-5 text-[#EC6E51]" />
                      <h3 className="text-lg font-semibold text-gray-900">Application Details</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedApplication.job?.title && (
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                            <Briefcase className="h-5 w-5 text-[#EC6E51]" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium mb-1">Job Title</p>
                            <p className="text-sm font-medium text-gray-900">{selectedApplication.job.title}</p>
                          </div>
                        </div>
                      )}
                      {selectedApplication.company?.companyName && (
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                            <Building2 className="h-5 w-5 text-[#EC6E51]" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium mb-1">Company</p>
                            <p className="text-sm font-medium text-gray-900">{selectedApplication.company.companyName}</p>
                          </div>
                        </div>
                      )}
                      {selectedApplication.currentStatus?.timestamp && (
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                            <Clock className="h-5 w-5 text-[#EC6E51]" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium mb-1">Last Updated</p>
                            <p className="text-sm font-medium text-gray-900">
                              {new Date(selectedApplication.currentStatus.timestamp).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit"
                              })}
                            </p>
                          </div>
                        </div>
                      )}
                      {selectedApplication.candidate?.profileSource && (
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                            <Globe className="h-5 w-5 text-[#EC6E51]" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 font-medium mb-1">Profile Source</p>
                            <p className="text-sm font-medium text-gray-900">{selectedApplication.candidate.profileSource}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Additional Details */}
                  {selectedApplication.candidate?.additionalDetails && (
                    <div className="bg-gray-50 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <FileText className="h-5 w-5 text-[#EC6E51]" />
                        <h3 className="text-lg font-semibold text-gray-900">Additional Details</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedApplication.candidate.additionalDetails.alternateMobileNumber && (
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                              <Phone className="h-5 w-5 text-[#EC6E51]" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 font-medium mb-1">Alternate Mobile</p>
                              <p className="text-sm font-medium text-gray-900">{selectedApplication.candidate.additionalDetails.alternateMobileNumber}</p>
                            </div>
                          </div>
                        )}
                        {selectedApplication.candidate.additionalDetails.panNumber && (
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                              <Hash className="h-5 w-5 text-[#EC6E51]" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 font-medium mb-1">PAN Number</p>
                              <p className="text-sm font-medium text-gray-900">{selectedApplication.candidate.additionalDetails.panNumber}</p>
                            </div>
                          </div>
                        )}
                        {selectedApplication.candidate.additionalDetails.vendorName && (
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                              <Building2 className="h-5 w-5 text-[#EC6E51]" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 font-medium mb-1">Vendor Name</p>
                              <p className="text-sm font-medium text-gray-900">{selectedApplication.candidate.additionalDetails.vendorName}</p>
                            </div>
                          </div>
                        )}
                        {selectedApplication.candidate.additionalDetails.region && (
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                              <MapPin className="h-5 w-5 text-[#EC6E51]" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 font-medium mb-1">Region</p>
                              <p className="text-sm font-medium text-gray-900">{selectedApplication.candidate.additionalDetails.region}</p>
                            </div>
                          </div>
                        )}
                        {selectedApplication.candidate.additionalDetails.permanentAddress?.fullAddress && (
                          <div className="flex items-start gap-3 md:col-span-2">
                            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                              <MapPin className="h-5 w-5 text-[#EC6E51]" />
                            </div>
                            <div className="flex-1">
                              <p className="text-xs text-gray-500 font-medium mb-1">Permanent Address</p>
                              <p className="text-sm font-medium text-gray-900">{selectedApplication.candidate.additionalDetails.permanentAddress.fullAddress}</p>
                            </div>
                          </div>
                        )}
                        {selectedApplication.candidate.additionalDetails.currentAddress?.fullAddress && (
                          <div className="flex items-start gap-3 md:col-span-2">
                            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                              <MapPin className="h-5 w-5 text-[#EC6E51]" />
                            </div>
                            <div className="flex-1">
                              <p className="text-xs text-gray-500 font-medium mb-1">Current Address</p>
                              <p className="text-sm font-medium text-gray-900">{selectedApplication.candidate.additionalDetails.currentAddress.fullAddress}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-6 bg-gray-50 rounded-b-2xl">
                  <div className="flex justify-end">
                    <Button 
                      variant="outline" 
                      onClick={() => setIsDetailsModalOpen(false)}
                      className="px-6"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
